import { makeStyles } from '@material-ui/core';
import { Grid } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import { useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Spin from 'components/Spin';
import { Formik, Form, Field } from 'formik';
import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';
import { of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import DialogTitle from 'v2/components/Shared/DialogTitle';
import { ListRadioButtonFormField } from 'v2/components/Shared/FormFields/ListRadioButton';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import Transition from 'v2/components/Shared/Transition';
import { numberToBRL } from 'v2/helpers/money';
import { ERefundMotiveId } from 'v2/interfaces/requestManagement/refund';
import { refundRequestFactory } from 'v2/services/shopping/refundRequest';
import * as Yup from 'yup';

import type { IInvoice } from 'v2/interfaces/invoice';
import type { ISendPartialRefund } from 'v2/interfaces/requestManagement/refund';

import type { FormikProps } from 'formik';

interface IProps {
  open: boolean;
  invoice: IInvoice;
  onClose: () => void;
}

interface IFormikValues {
  motive_id: ERefundMotiveId;
  description: string;
  amount: number;
}

const useStyles = makeStyles((theme) => ({
  dialogActions: {
    padding: `${theme.spacing(1)}px ${theme.spacing(3)}px`,
  },
}));

export function PartialRefundRequestModal({ open, invoice, onClose }: IProps) {
  const classes = useStyles();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [motives] = useObservable(() => refundRequestFactory.getRefundMotive(), []);
  const [loading] = useObservable(() => refundRequestFactory.loading(), []);

  const onHandleClose = useCallback(
    (resetForm) => {
      onClose();
      resetForm();
    },
    [onClose]
  );

  const [onSubmit] = useCallbackObservable(
    (values: IFormikValues, { setSubmitting, resetForm }: FormikProps<IFormikValues>) => {
      setSubmitting(true);

      let { description } = motives.find((motive) => motive.id === values.motive_id) || {};

      if (values.motive_id === ERefundMotiveId.OTHER_REASON) {
        description += ` - ${values.description}`;
      }

      const invoice_id = invoice.id || invoice.invoice_id;

      const formattedValues: ISendPartialRefund = {
        amount: values.amount,
        motive_id: values.motive_id,
        invoice_id,
        description,
      };

      return refundRequestFactory.sendPartialRefundRequest(formattedValues).pipe(
        tap(() => {
          setSubmitting(false);
          onHandleClose(resetForm);
          toast.success('Solicitação realizada com sucesso!');
        }),
        catchError((err) => {
          setSubmitting(false);
          console.error(err);
          toast.error('Não foi possível reembolsar parcialmente a fatura.');
          return of(true);
        })
      );
    },
    [motives, invoice]
  );

  const minAmountValue = 0.01;
  const formattedMinAmountValue = numberToBRL(minAmountValue);
  const maxAmountValue = invoice.sale_value;
  const formattedMaxAmountValue = numberToBRL(invoice.sale_value);

  const validationSchema = Yup.object().shape({
    motive_id: Yup.string().required('Selecione pelo menos um motivo.'),
    description: Yup.string().when('motive_id', {
      is: (v: string) => v === ERefundMotiveId.OTHER_REASON,
      then: Yup.string().required('Insira os detalhes do reembolso.').min(5),
    }),
    amount: Yup.number()
      .required()
      .min(minAmountValue, `O valor deve ser no mínimo ${formattedMinAmountValue}`)
      .max(maxAmountValue, `O valor deve ser no máximo ${formattedMaxAmountValue}`),
  });

  const motiveOptions = (motives || []).map((item) => ({
    label: item.description,
    value: item.id,
  }));

  return (
    <Formik validationSchema={validationSchema} initialValues={{ motive_id: '', description: '', amount: 0 }} onSubmit={onSubmit}>
      {({ isSubmitting, submitForm, values, resetForm, dirty, isValid }) => {
        const otherReasonSelected = values.motive_id === ERefundMotiveId.OTHER_REASON;
        const shouldDisableConfirmBtn = !isValid || isSubmitting || !dirty;

        return (
          <Form>
            <Dialog
              id='partial-refund-dialog'
              TransitionComponent={Transition}
              open={open}
              onClose={() => onHandleClose(resetForm)}
              fullScreen={fullScreen}
              fullWidth={true}
            >
              <DialogTitle id='partial-refund-dialog-title' onClose={() => onHandleClose(resetForm)}>
                <Typography variant='h6'>Deseja realmente reembolsar parcialmente essa fatura?</Typography>
              </DialogTitle>
              <DialogContent dividers>
                {loading || isSubmitting ? (
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <Spin />
                  </div>
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Field
                        id='amount'
                        name='amount'
                        mask='money'
                        label='Valor a ser reembolsado:'
                        component={TextFormField}
                        placeholder='R$ 0,00'
                        helperText='O valor do reembolso deve ser menor ou igual ao valor da fatura.'
                        InputProps={{
                          max: invoice.sale_value,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Field
                        id='motive_id'
                        name='motive_id'
                        options={motiveOptions}
                        label='Informe o motivo do reembolso:'
                        component={ListRadioButtonFormField}
                      />
                      {otherReasonSelected && (
                        <Field
                          id='description'
                          name='description'
                          multiline
                          fullWidth
                          InputProps={{ rows: 3, disableUnderline: true }}
                          InputLabelProps={{ shrink: true }}
                          placeholder='Descreva o motivo do reembolso dessa fatura. (Lembre-se de que essa informação será direcionada ao cliente.)'
                          component={TextFormField}
                        />
                      )}
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button color='default' onClick={() => onHandleClose(resetForm)}>
                  Cancelar
                </Button>
                <Button onClick={submitForm} type='submit' variant='contained' color='secondary' disabled={shouldDisableConfirmBtn}>
                  Confirmar
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
}
